import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Box } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { FieldError, FormProvider, useForm } from 'react-hook-form';
import { defaultNewCustomer } from 'shared/constants';
import { FormTextField, SelectField } from 'shared/controls';
import { useCreateCustomer } from 'shared/mutations/create-customer';
import {
  useQueryCustomerBrands,
  useQueryCustomerBrandsByCustomer,
  useQueryCustomerTiers,
  useQueryCustomerChannels,
} from 'shared/queries';
import { CustomerManagementGridItem, NewCustomerForm } from 'shared/types';

interface NewCustomerDialogProps {
  isOpen: boolean;
  toggleDialog: () => void;
  isExisting: boolean;
  customers: CustomerManagementGridItem[];
}

export function NewCustomerDialog({ isOpen, toggleDialog, isExisting, customers }: NewCustomerDialogProps) {
  const methods = useForm<NewCustomerForm>({
    defaultValues: defaultNewCustomer,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = methods;

  useEffect(() => {
    reset();
  }, [isOpen, reset]);

  const onClose = useCallback(() => {
    reset();
    toggleDialog();
  }, [toggleDialog, reset]);

  const customerMutation = useCreateCustomer();

  const onSubmit = ({ brands, abgmastercustomer, ...customer }: NewCustomerForm) => {
    brands.forEach(async brand => {
      await customerMutation.mutateAsync({
        ...customer,
        brand,
        abgmastercustomer: abgmastercustomer.toUpperCase(),
        uniqueid: abgmastercustomer.toUpperCase() + brand + customer.channel,
      });
    });
    toggleDialog();
  };

  let { brandOptions, isFetching: isFetchingBrands, isLoading: isLoadingBrands } = useQueryCustomerBrands();
  const customer = watch('abgmastercustomer');
  const { customerBrands } = useQueryCustomerBrandsByCustomer(isExisting ? customer : '');

  brandOptions = useMemo(() => {
    if (customerBrands?.length) {
      return brandOptions?.filter(brand => !customerBrands.includes(brand));
    }
    return brandOptions;
  }, [brandOptions, customerBrands]);

  const customerOptions = useMemo(() => {
    return [...new Set(customers.map(customer => customer.masterCustomer))].sort();
  }, [customers]);

  const { tierOptions, isFetching: isFetchingTiers, isLoading: isLoadingTiers } = useQueryCustomerTiers();
  const { channelOptions, isFetching: isFetchingChannels, isLoading: isLoadingChannels } = useQueryCustomerChannels();
  const isLoading =
    isFetchingBrands || isLoadingBrands || isFetchingChannels || isLoadingChannels || isFetchingTiers || isLoadingTiers;

  return (
    <Dialog open={isOpen && !isLoading} onClose={onClose} fullWidth aria-labelledby='new-customer-dialog'>
      <DialogTitle id='new-customer-dialog-title'>
        {isExisting ? 'Add To Existing Customer' : 'New Customer'}
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id='new-customer-form' onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ marginTop: 1 }}>
              {isExisting ? (
                <SelectField
                  name='abgmastercustomer'
                  label='Customer'
                  options={customerOptions}
                  getOptionLabel={opt => opt}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return true;
                    return option === value;
                  }}
                  rules={{
                    required: { message: 'Customer is required', value: true },
                  }}
                  error={Boolean(errors?.abgmastercustomer)}
                  helperText={errors?.abgmastercustomer?.message}
                />
              ) : (
                <FormTextField
                  name='abgmastercustomer'
                  label='Customer'
                  fullWidth
                  rules={{
                    required: { message: 'Customer is required', value: true },
                  }}
                  error={Boolean(errors?.abgmastercustomer)}
                  helperText={errors?.abgmastercustomer?.message}
                  sx={{ input: { textTransform: 'uppercase' } }}
                />
              )}
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                disabled={isExisting && !customer}
                name='brands'
                label='Brand'
                options={brandOptions || []}
                multiple
                disableCloseOnSelect
                loading={isFetchingBrands || isLoadingBrands}
                getOptionLabel={opt => opt}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                rules={{
                  required: { message: 'Brand is required', value: true },
                }}
                error={Boolean(errors?.brands)}
                helperText={(errors?.brands as FieldError | undefined)?.message}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='customertier'
                label='Tier'
                loading={isFetchingTiers || isLoadingTiers}
                options={tierOptions || []}
                getOptionLabel={opt => opt}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                rules={{
                  required: { message: 'Tier is required', value: true },
                }}
                error={Boolean(errors?.customertier)}
                helperText={errors?.customertier?.message}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='channel'
                label='Channel'
                loading={isFetchingChannels || isLoadingChannels}
                options={channelOptions || []}
                getOptionLabel={opt => opt}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                rules={{
                  required: { message: 'Channel is required', value: true },
                }}
                error={Boolean(errors?.channel)}
                helperText={errors?.channel?.message}
              />
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Cancel
        </Button>
        <Button type='submit' form='new-customer-form' variant='contained' color='primary'>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
